import { useEffect, useRef, useState } from 'react';
import { Spinner } from '@primer/react';
import storage from '@/utils/storage';
import { useRefreshAccessToken } from '@/features/auth/api/refreshAccessToken';
import { jwtDecode } from 'jwt-decode';

interface AccessTokenPayload {
  exp: number;
}
const MAX_RETRIES = 4;
const REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes before expiry
const PERIODIC_CHECK_INTERVAL = 30 * 1000; // Check every 30 seconds

/** Returns the ms‐timestamp when the current access‐JWT actually expires. */
function getAccessTokenExpiryMs(): number | null {
  const raw = storage.getAccessToken();
  if (!raw) return null;
  try {
    const { exp } = jwtDecode<AccessTokenPayload>(raw);
    return exp * 1000;
  } catch {
    return null;
  }
}

/** Returns the ms‐timestamp when the current id‐token actually expires. */
function getIdTokenExpiryMs(): number | null {
  const raw = storage.getIdToken();
  if (!raw) return null;
  try {
    const { exp } = jwtDecode<AccessTokenPayload>(raw);
    return exp * 1000;
  } catch {
    return null;
  }
}

/** Check if either access token or id token needs refresh (5 minutes before expiry) */
function shouldRefreshTokens(): boolean {
  const accessExpMs = getAccessTokenExpiryMs();
  const idExpMs = getIdTokenExpiryMs();
  const now = Date.now();

  // If we have tokens, check if either expires within 5 minutes
  if (accessExpMs && accessExpMs - now <= REFRESH_THRESHOLD_MS) {
    return true;
  }

  if (idExpMs && idExpMs - now <= REFRESH_THRESHOLD_MS) {
    return true;
  }

  return false;
}

export const AccessTokenRefresh = () => {
  const { mutateAsync, isLoading } = useRefreshAccessToken();
  const timer = useRef<number | null>(null);
  const periodicCheckTimer = useRef<number | null>(null);
  const didInitialRefresh = useRef(false);
  const [waitingForToken, setWaitingForToken] = useState(false);
  const lastRefreshTime = useRef<number>(0);
  const isRefreshing = useRef(false); // Add flag to prevent concurrent refreshes

  /**
   * Try refreshing, retrying up to `retriesLeft` times on error
   */
  async function doRefresh(retriesLeft = MAX_RETRIES, showSpinner = false) {
    // prevent concurrent calls and avoid too frequent refreshes
    if (isLoading || isRefreshing.current || Date.now() - lastRefreshTime.current < 10000) {
      console.log('Refresh blocked', {
        isLoading,
        isRefreshing: isRefreshing.current,
        timeSinceLastRefresh: Date.now() - lastRefreshTime.current,
      });
      return;
    }

    const rt = storage.getRefreshToken();
    if (!rt) {
      setWaitingForToken(false);
      scheduleNext();
      return;
    }

    if (showSpinner) {
      setWaitingForToken(true); // Only show spinner when explicitly requested
    }

    try {
      isRefreshing.current = true;
      lastRefreshTime.current = Date.now();
      const result = await mutateAsync(rt);

      // Verify tokens were actually updated in storage
      const newAccessToken = storage.getAccessToken();
      const newIdToken = storage.getIdToken();

      console.log('Token refresh successful', {
        hasNewAccessToken: !!newAccessToken,
        hasNewIdToken: !!newIdToken,
        timestamp: new Date().toISOString(),
      });

      // Check if we should reload the page based on access token and session storage
      const hasAccessFlag = storage.getIsHasProjectAccess();

      // Show spinner for 2 more seconds after token is set (only if spinner was shown)
      if (newAccessToken && showSpinner) {
        if (hasAccessFlag) {
          console.log('Access token fetched and is_has_access is true - reloading page');
          setTimeout(() => {
            window.location.reload();
          }, 1000);
          return; // Exit early since we're reloading
        } else {
          setTimeout(() => {
            setWaitingForToken(false);
          }, 2000);
        }
      } else if (showSpinner) {
        console.log('Warning: Refresh completed but no access token found in storage');
        setWaitingForToken(false);
      }

      isRefreshing.current = false;
      // on success: schedule the next refresh based on new token
      scheduleNext();
    } catch (err) {
      console.error('Refresh attempt failed', { error: err, retriesLeft });
      isRefreshing.current = false;

      if (retriesLeft > 0) {
        // retry with exponential backoff
        const delay = (MAX_RETRIES - retriesLeft + 1) * 2000;
        console.warn(`Refresh failed, retrying in ${delay}ms (${retriesLeft} left)`, err);
        // Keep spinner visible during retry only if it was initially shown
        setTimeout(() => doRefresh(retriesLeft - 1, showSpinner), delay);
      } else {
        console.error('Refresh failed after maximum retries', err);
        if (showSpinner) {
          setWaitingForToken(false);
        }
        // give up and schedule the next "normal" refresh
        scheduleNext();
      }
    }
  }

  /** Schedules the next attempt 5 minutes before expiry */
  function scheduleNext() {
    if (timer.current) clearTimeout(timer.current);

    const accessExpMs = getAccessTokenExpiryMs();
    const idExpMs = getIdTokenExpiryMs();
    const hasRefreshToken = !!storage.getRefreshToken();

    // If no access token but has refresh token → refresh immediately with spinner
    if (!accessExpMs && hasRefreshToken && !isRefreshing.current) {
      console.log('No access token but refresh token available - triggering refresh');
      doRefresh(MAX_RETRIES, true).then(() => {
        // Check if we now have a token after refresh
        const newAccessToken = storage.getAccessToken();
        const hasAccessFlag = storage.getIsHasProjectAccess();

        if (newAccessToken) {
          if (hasAccessFlag) {
            console.log('Access token obtained and is_has_access is true - reloading page');
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } else {
            console.log('Access token obtained, reloading page');
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        } else {
          console.log('Still no access token after refresh attempt');
          // Schedule another check in case refresh failed
          timer.current = window.setTimeout(scheduleNext, 5000);
        }
      });
      return;
    }

    // If no valid tokens and no refresh token → poll until one appears
    if (!accessExpMs && !idExpMs && !hasRefreshToken) {
      console.log('No tokens available, scheduling next check');
      timer.current = window.setTimeout(scheduleNext, PERIODIC_CHECK_INTERVAL);
      return;
    }

    // If we have tokens, schedule based on expiry
    if (accessExpMs || idExpMs) {
      // Find the earliest expiry time
      const earliestExpiry = Math.min(...([accessExpMs, idExpMs].filter(Boolean) as number[]));
      const delay = earliestExpiry - REFRESH_THRESHOLD_MS - Date.now();

      if (delay <= 0) {
        // Token expires within 5 minutes or has already expired
        console.log('Token refresh needed immediately', {
          accessExpiry: accessExpMs ? new Date(accessExpMs).toISOString() : null,
          idExpiry: idExpMs ? new Date(idExpMs).toISOString() : null,
          now: new Date().toISOString(),
        });
        // Don't show spinner for scheduled refresh - user can continue using the app
        doRefresh(MAX_RETRIES, false);
      } else {
        console.log('Token refresh scheduled', {
          delay: Math.round(delay / 1000),
          scheduledTime: new Date(Date.now() + delay).toISOString(),
        });
        timer.current = window.setTimeout(() => doRefresh(MAX_RETRIES, false), delay);
      }
    }
  }

  /** Periodically check expiration and schedule refresh if needed */
  function startPeriodicCheck() {
    if (periodicCheckTimer.current) clearInterval(periodicCheckTimer.current);
    periodicCheckTimer.current = window.setInterval(() => {
      const accessExpMs = getAccessTokenExpiryMs();
      const hasRefreshToken = !!storage.getRefreshToken();

      // Check if we lost our access token but still have refresh token
      if (!accessExpMs && hasRefreshToken && !isRefreshing.current) {
        console.log('Periodic check: access token missing but refresh token available');
        doRefresh(MAX_RETRIES, true);
        return;
      }

      if (shouldRefreshTokens()) {
        console.log('Periodic check: tokens need refresh - initiating refresh');
        // Don't show spinner for periodic refresh - user can continue using the app
        doRefresh(MAX_RETRIES, false);
      }
    }, PERIODIC_CHECK_INTERVAL);
  }

  useEffect(() => {
    // Check if we need to show spinner initially (no access token but has refresh token)
    const accessToken = storage.getAccessToken();
    const refreshToken = storage.getRefreshToken();

    if (!accessToken && refreshToken) {
      console.log('Initial load: no access token but refresh token available');
      setWaitingForToken(true);
    }

    // detect a hard reload
    const nav = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const isReload =
      nav.length > 0
        ? nav[0].type === 'reload'
        : // fallback for older browsers
          performance.navigation?.type === performance.navigation.TYPE_RELOAD;

    if (isReload && !didInitialRefresh.current) {
      didInitialRefresh.current = true;
      console.log('Page reload detected, refreshing tokens');
      // Don't show spinner on reload unless access token is missing
      doRefresh(MAX_RETRIES, !accessToken);
    } else {
      // Check if tokens need immediate refresh or if access token is missing
      const needsRefresh = shouldRefreshTokens();
      const missingAccessToken = !accessToken && !!refreshToken;

      if (needsRefresh || missingAccessToken) {
        console.log('Initial load: tokens need refresh or access token missing', {
          needsRefresh,
          missingAccessToken,
        });
        // Only show spinner if access token is completely missing
        doRefresh(MAX_RETRIES, missingAccessToken);
      } else {
        scheduleNext();
      }
    }

    // Start periodic check
    startPeriodicCheck();

    return () => {
      if (timer.current) clearTimeout(timer.current);
      if (periodicCheckTimer.current) clearInterval(periodicCheckTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (waitingForToken) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'white',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spinner size="large" />
      </div>
    );
  }

  return null;
};
